import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import {
    buildMonthlyExpenseSeries,
    expenseDisplayFactor,
    type ExpenseMonthlyPoint,
} from '../../domain/statisticsExpenses';
import ChartPlaceholder from './ChartPlaceholder';

interface MonthlyExpensesChartProps {
    readonly data: readonly ExpenseMonthlyPoint[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
    readonly exchangeRate?: number;
}

function MonthlyExpensesChart({
    data,
    loading = false,
    currency = 'LEI',
    exchangeRate = 1,
}: MonthlyExpensesChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} height={350} />;
    }

    const { periods, series } = buildMonthlyExpenseSeries(
        data,
        expenseDisplayFactor(currency, exchangeRate),
        type => t(`statistics.expenses.types.${type}`, type),
    );
    const suffix =
        currency === 'EUR' ? t('statistics.currencyEur', ' EUR') : t('statistics.currencyLei', ' Lei');

    const options: Highcharts.Options = {
        chart: { type: 'column', height: 350 },
        title: { text: undefined },
        xAxis: { categories: periods, crosshair: true },
        yAxis: {
            min: 0,
            title: { text: suffix.trim() },
            stackLabels: { enabled: true },
        },
        tooltip: { shared: true, valueSuffix: suffix },
        plotOptions: { column: { stacking: 'normal' } },
        legend: { enabled: true },
        series: series.map(entry => ({
            type: 'column' as const,
            name: entry.name,
            data: entry.data,
        })),
        credits: { enabled: false },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default MonthlyExpensesChart;
